"use client";

import Img from '@/components/Img';
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import LanguageSwitcher from "./LanguageSwitcher";
import Logo from "./Logo";
import { useLocale } from "@/lib/locale-context";
import { track } from "@/lib/metrika";

const NAV = [
  { href: "/#rent", key: "nav.rent" },
  { href: "/#stations", key: "nav.offices" },
  { href: "/about/faq", key: "nav.faq" },
  { href: "/news", key: "nav.news" },
  { href: "/blog", key: "nav.blog" },
  { href: "/about", key: "nav.about" },
];

// Бургер-меню (по образцу Солнышко): две колонки ссылок. Пункты-заглушки ведут
// на существующие разделы/якоря — пользователь дополнит маршруты.
// «О компании» и «Блог» не дублируем — они в NAV (верхнее меню / мобильный
// бургер сверху).
const MENU_COL1 = [
  { href: "/conditions", key: "nav.conditions" },
  { href: "/prices", key: "nav.prices" },
  { href: "/documents", key: "nav.documents" },
  { href: "/corporate", key: "menu.corporateLong" },
  { href: "/payment", key: "menu.payment" },
];
const MENU_COL2: { href: string; key: string; feedback?: boolean }[] = [
  { href: "/about/loyalty", key: "menu.loyalty" },
  { href: "/about/bonus", key: "menu.bonus" },
  { href: "/support", key: "nav.support" },
  { href: "/contacts", key: "nav.contacts" },
  { href: "#feedback", key: "nav.feedback", feedback: true },
];
// Соцсети — как в футере: цветной кружок (бренд-фон) + белая иконка.
const SOCIALS: { name: string; bg: string; icon: string; full?: boolean }[] = [
  { name: "vk", bg: "#0077ff", icon: "/icons/social/vk.svg" },
  { name: "tg", bg: "#26a5e4", icon: "/icons/social/tg.svg" },
  { name: "ok", bg: "#ee8208", icon: "/icons/social/ok.svg" },
  { name: "yt", bg: "#ff0000", icon: "/icons/social/yt.svg" },
  { name: "rutube", bg: "#100943", icon: "/icons/social/rutube.svg" },
  { name: "tiktok", bg: "#000000", icon: "/icons/social/tiktok.svg" },
  { name: "wa", bg: "#25d366", icon: "/icons/social/wa.svg" },
  { name: "max", bg: "transparent", icon: "/icons/social/max.svg", full: true },
];

type Contacts = { phone?: string; email?: string; socials?: Record<string, string> };

// theme: "dark" — поверх hero на главной (светлый текст); "light" — на светлых
// под-страницах (тёмный текст и логотип).
export default function Header({ theme = "dark" }: { theme?: "dark" | "light" }) {
  const light = theme === "light";
  const { t, href, locale } = useLocale();
  const pathname = usePathname() || "/";
  // Путь без префикса локали — для мобильного переключателя языка в бургере.
  const langBase = (pathname === "/en" || pathname.startsWith("/en/")) ? (pathname.replace(/^\/en/, "") || "/") : pathname;
  // Пункт «Партнёрам» — показываем, если страница включена в админке
  // (флаг window.__apPartners из layout; по умолчанию включено).
  const [partnersOn, setPartnersOn] = useState(true);
  useEffect(() => { if (typeof window !== "undefined" && (window as { __apPartners?: boolean }).__apPartners === false) setPartnersOn(false); }, []);
  const navItems = partnersOn ? [...NAV, { href: "/partners", key: "nav.partners" }] : NAV;
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState<Contacts>({});
  const headerRef = useRef<HTMLElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  // На мобиле выпадающую панель рендерим fixed (а не absolute) — иначе её режет
  // overflow-hidden Hero-баннера на главной. panelTop = низ шапки.
  const [isMobile, setIsMobile] = useState(false);
  const [panelTop, setPanelTop] = useState(72);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const upd = () => { setIsMobile(mq.matches); if (barRef.current) setPanelTop(barRef.current.getBoundingClientRect().bottom); };
    upd();
    mq.addEventListener("change", upd);
    window.addEventListener("resize", upd);
    return () => { mq.removeEventListener("change", upd); window.removeEventListener("resize", upd); };
  }, []);

  // Контакты футера/хедера — из глобальных настроек (rent_site_global.contacts).
  useEffect(() => {
    fetch("/api/v1/rent-site/settings", { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.settings?.contacts) setContacts(d.settings.contacts); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    if (barRef.current) setPanelTop(barRef.current.getBoundingClientRect().bottom);
    const onDoc = (e: MouseEvent) => { if (headerRef.current && !headerRef.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    // На мобиле меню — полноэкранный оверлей: блокируем прокрутку страницы под
    // ним, скроллится только сама панель.
    const lock = isMobile;
    if (lock) document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      if (lock) document.body.style.overflow = "";
    };
  }, [open, isMobile]);

  const phone = contacts.phone || "8 800 444-48-48";
  const phoneTel = phone.replace(/[^+\d]/g, "");
  const email = contacts.email || "";
  const socials = SOCIALS.map((s) => ({ ...s, href: (contacts.socials || {})[s.name] })).filter((s) => s.href);

  // Цвета меню: на главной (dark) — тёмное стекло + БЕЛЫЙ текст; на подстраницах
  // (light) — светлое стекло + СИНИЙ текст.
  const glassBg = light ? "rgba(255,255,255,0.72)" : "rgba(13,20,42,0.55)";
  const glassFilter = "blur(22px) saturate(140%)";
  const menuText = light ? "text-primary" : "text-white";
  const menuBorder = light ? "border-black/10" : "border-white/15";

  return (
    <header ref={headerRef} className="relative z-50 w-full">
      {/* при открытии бургера сама шапка тоже становится стеклом */}
      <div
        ref={barRef}
        className="transition-colors duration-300"
        style={open ? { background: glassBg, backdropFilter: glassFilter, WebkitBackdropFilter: glassFilter } : undefined}
      >
        <div className="mx-auto flex max-w-[1200px] items-center justify-between gap-6 px-5 py-5 sm:px-8 lg:py-6 xl:px-0">
          <div className="flex items-center gap-4 lg:gap-6">
            <Link href={href("/")} aria-label={locale === "en" ? "Home" : "На главную"}>
              <Logo variant={light ? "dark" : "light"} />
            </Link>
            <nav className="hidden items-center xl:flex">
              {navItems.map((l) => (
                <a
                  key={l.href}
                  href={href(l.href)}
                  className={`px-[11px] py-4 text-[16px] leading-[24px] transition ${light ? "text-text hover:text-primary" : "text-text-on-dark hover:text-white"}`}
                >
                  {t(l.key)}
                </a>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <LanguageSwitcher theme={theme} />
            <a
              href={`tel:${phoneTel}`}
              onClick={() => track("phone_click")}
              className={`hidden h-11 items-center justify-center rounded-xl px-4 text-[16px] leading-6 transition md:flex ${light ? "bg-primary text-white hover:bg-primary-hover" : "bg-primary-soft text-text-on-dark hover:bg-primary"}`}
            >
              {phone}
            </a>
            <button
              type="button"
              aria-label={locale === "en" ? "Menu" : "Меню"}
              aria-expanded={open}
              onClick={() => { if (!open) track("menu_open"); setOpen((o) => !o); }}
              className={`flex size-11 cursor-pointer items-center justify-center rounded-xl transition ${light ? "bg-[#EAF0FB] text-text hover:bg-[#DCE6F8]" : "bg-white/20 text-text-on-dark hover:bg-white/30"}`}
            >
              {open ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              ) : (
                <span className="relative block h-3.5 w-5">
                  <span className="absolute inset-x-0 top-0 h-0.5 bg-current" />
                  <span className="absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 bg-current" />
                  <span className="absolute inset-x-0 bottom-0 h-0.5 bg-current" />
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Стеклянная панель меню — фон/текст зависят от темы (главная — белый текст). */}
      <div
        className={
          isMobile
            ? `fixed inset-x-0 z-[60] overflow-y-auto border-t ${menuBorder} transition-opacity duration-300 ${open ? "opacity-100" : "pointer-events-none opacity-0"}`
            : `absolute inset-x-0 top-full z-[60] border-t ${menuBorder} transition-[max-height,opacity] duration-300 ${open ? "max-h-[480px] overflow-y-auto opacity-100" : "pointer-events-none max-h-0 overflow-hidden opacity-0"}`
        }
        style={{
          background: glassBg, backdropFilter: glassFilter, WebkitBackdropFilter: glassFilter,
          ...(isMobile ? { top: panelTop, bottom: 0 } : {}),
        }}
      >
        <div className={`mx-auto flex max-w-[1200px] flex-col gap-6 px-5 py-7 pb-10 sm:px-8 lg:flex-row lg:items-start lg:gap-0 lg:pb-7 xl:px-0 ${menuText}`}>
          {/* контакты слева */}
          <div className="flex flex-col lg:mr-auto">
            <a href={`tel:${phoneTel}`} onClick={() => track("phone_click")} className="mb-3 flex items-center gap-2 text-[18px] font-bold">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13.83 16.57a1 1 0 0 0 1.21-.3l.36-.47A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.47.35a1 1 0 0 0-.29 1.23 14 14 0 0 0 6.39 6.39" /></svg>
              {phone}
            </a>
            {email && (
              <a href={`mailto:${email}`} onClick={() => track("email_click")} className="mb-8 flex items-center gap-2 text-[16px] font-medium">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 7-8.99 5.73a2 2 0 0 1-2.01 0L2 7" /><rect x="2" y="4" width="20" height="16" rx="2" /></svg>
                {email}
              </a>
            )}
            {socials.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                {socials.map((s) => (
                  <a key={s.name} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.name} onClick={() => track(s.name === "wa" ? "whatsapp_click" : s.name === "tg" ? "telegram_click" : "social_click")} className="flex size-10 items-center justify-center overflow-hidden rounded-full transition hover:opacity-90" style={{ background: s.bg }}>
                    {s.full
                      ? <Img src={s.icon} alt={s.name} width={40} height={40} className="size-10 object-contain" />
                      : <Img src={s.icon} alt={s.name} width={20} height={20} className="size-5" style={{ filter: "brightness(0) invert(1)" }} />}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* обёртка ссылок: на мобиле — единый сплошной список (две колонки
              сливаются), на десктопе через lg:contents — снова две колонки. */}
          <div className="flex w-full flex-col lg:contents">
          {/* Верхнее меню (NAV) — только в мобильном бургере (на десктопе оно в
              топ-баре). На lg прячем через lg:hidden. */}
          <div className="flex w-full flex-col lg:hidden">
            {navItems.map((l, i) => (
              <Link key={`nav-${i}`} href={href(l.href)} onClick={() => setOpen(false)} className={`border-b ${menuBorder} py-2 text-[16px] transition hover:opacity-70`}>
                {t(l.key)}
              </Link>
            ))}
            {/* Переключатель языка — в бургере на мобиле (в топ-баре скрыт на <sm) */}
            <div className="flex items-center gap-2 py-3 sm:hidden">
              <a href={langBase} onClick={() => { track("lang_switch"); setOpen(false); }} className={`rounded-lg px-3 py-1.5 text-[15px] font-medium transition ${locale === "ru" ? "bg-primary text-white" : `border ${menuBorder}`}`}>RU</a>
              <a href={langBase === "/" ? "/en" : `/en${langBase}`} onClick={() => { track("lang_switch"); setOpen(false); }} className={`rounded-lg px-3 py-1.5 text-[15px] font-medium transition ${locale === "en" ? "bg-primary text-white" : `border ${menuBorder}`}`}>EN</a>
            </div>
          </div>
          {/* колонка ссылок 1 */}
          <div className="flex w-full flex-col lg:w-[280px]">
            {MENU_COL1.map((l, i) => (
              <Link key={i} href={href(l.href)} onClick={() => setOpen(false)} className={`border-b ${menuBorder} py-2 text-[16px] transition last:border-0 hover:opacity-70`}>
                {t(l.key)}
              </Link>
            ))}
          </div>

          {/* колонка ссылок 2 (на десктопе — по правому краю, на мобиле — слева) */}
          <div className="flex w-full flex-col text-left lg:w-[280px] lg:text-right">
            {MENU_COL2.map((l, i) =>
              l.feedback ? (
                <button
                  key={i}
                  type="button"
                  onClick={() => { track("feedback_open"); setOpen(false); window.dispatchEvent(new Event("open-feedback")); }}
                  className={`border-b ${menuBorder} py-2 text-left text-[16px] transition last:border-0 hover:opacity-70 lg:text-right`}
                >
                  {t(l.key)}
                </button>
              ) : (
                <Link key={i} href={href(l.href)} onClick={() => setOpen(false)} className={`border-b ${menuBorder} py-2 text-[16px] transition last:border-0 hover:opacity-70`}>
                  {t(l.key)}
                </Link>
              ),
            )}
          </div>
          </div>
        </div>
      </div>
    </header>
  );
}
